import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import {
	Paper, Table, TableBody, TableCell, TableHead, TableRow,
	TextField, Button, IconButton, Typography, Grid, Snackbar,
	CircularProgress, Toolbar
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import SyncIcon from '@material-ui/icons/Sync'
import Alert from '@material-ui/lab/Alert'
import { ADD_COMMON_NAME, DELETE_COMMON_NAME } from '../../libs/Mutations'
import { SERVERIP } from '../../constants'

const COMMON_NAMES_QUERY = gql`
query get_common_names($manufacturerId: Int!) {
  admin(manufacturerId: $manufacturerId) {
    commonNames(manufacturerId: $manufacturerId) {
      nameId
      cerName
      manufacturerId
      isActive
    }
  }
}
`

const styles = (theme) => ({
	root: {
		padding: theme.spacing(2),
	},
	toolbar: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(2),
	},
	addForm: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	addInput: {
		minWidth: 300,
	},
	table: {
		minWidth: 400,
	},
	emptyState: {
		padding: theme.spacing(4),
		textAlign: 'center',
		color: theme.palette.grey[500],
	},
	syncButton: {
		marginLeft: theme.spacing(1),
	},
})

const CommonNameManager = ({ classes, manufacturerId }) => {
	const [newName, setNewName] = useState('')
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
	const [syncing, setSyncing] = useState(false)

	const { data, loading, error, refetch } = useQuery(
		COMMON_NAMES_QUERY,
		{
			variables: { manufacturerId: parseInt(manufacturerId) },
			fetchPolicy: 'network-only',
		}
	)

	const [addCommonName, { loading: adding }] = useMutation(ADD_COMMON_NAME, {
		onCompleted: () => {
			setNewName('')
			refetch()
			setSnackbar({ open: true, message: 'Common name added', severity: 'success' })
		},
		onError: (err) => {
			setSnackbar({ open: true, message: err.message, severity: 'error' })
		},
	})

	const [deleteCommonName, { loading: deleting }] = useMutation(DELETE_COMMON_NAME, {
		onCompleted: () => {
			refetch()
			setSnackbar({ open: true, message: 'Common name removed', severity: 'success' })
		},
		onError: (err) => {
			setSnackbar({ open: true, message: err.message, severity: 'error' })
		},
	})

	const handleAdd = () => {
		const trimmed = newName.trim()
		if (!trimmed) return
		addCommonName({
			variables: {
				manufacturerId: parseInt(manufacturerId),
				cerName: trimmed,
			},
		})
	}

	const handleDelete = (nameId) => {
		if (!window.confirm('Remove this common name?')) return
		deleteCommonName({
			variables: {
				nameId: parseInt(nameId),
				manufacturerId: parseInt(manufacturerId),
			},
		})
	}

	const handleSync = async () => {
		setSyncing(true)
		try {
			const token = localStorage.getItem('id_token')
			const response = await fetch(
				`${SERVERIP}/api/Profile/sync_manufacturer_products?manufacturerId=${manufacturerId}`,
				{
					method: 'GET',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...(token ? { 'Authorization': 'Bearer ' + token } : {}),
					},
				}
			)
			if (response.ok) {
				setSnackbar({ open: true, message: 'Product sync completed', severity: 'success' })
			} else {
				setSnackbar({ open: true, message: 'Sync failed', severity: 'error' })
			}
		} catch {
			setSnackbar({ open: true, message: 'Sync request failed', severity: 'error' })
		} finally {
			setSyncing(false)
		}
	}

	const commonNames = data?.admin?.commonNames || []

	if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><CircularProgress /></div>
	if (error) return <Typography color="error" style={{ padding: 16 }}>Failed to load common names</Typography>

	return (
		<div className={classes.root}>
			<div className={classes.toolbar}>
				<Typography variant="h6">
					CEC Brand Names ({commonNames.length})
				</Typography>
				<Button
					variant="outlined"
					color="primary"
					startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />}
					onClick={handleSync}
					disabled={syncing}
					className={classes.syncButton}
				>
					Sync Products
				</Button>
			</div>

			<Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
				These are the brand names used to match and import products from the CEC approved list.
				Add alternate names if a manufacturer uses different brand names for batteries and inverters.
			</Typography>

			<Grid container spacing={1} alignItems="center" className={classes.addForm}>
				<Grid item>
					<TextField
						className={classes.addInput}
						variant="outlined"
						size="small"
						label="New brand name"
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						onKeyPress={(e) => { if (e.key === 'Enter') handleAdd() }}
						disabled={adding}
					/>
				</Grid>
				<Grid item>
					<Button
						variant="contained"
						color="primary"
						startIcon={adding ? <CircularProgress size={18} /> : <AddIcon />}
						onClick={handleAdd}
						disabled={adding || !newName.trim()}
					>
						Add
					</Button>
				</Grid>
			</Grid>

			{commonNames.length === 0 ? (
				<Paper elevation={0} className={classes.emptyState}>
					<Typography variant="body1">No common names configured yet.</Typography>
					<Typography variant="body2" color="textSecondary">
						Add the manufacturer's CEC brand name to start importing products.
					</Typography>
				</Paper>
			) : (
				<Paper elevation={0} variant="outlined">
					<Table className={classes.table} size="small">
						<TableHead>
							<TableRow>
								<TableCell>Brand Name (CerName)</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{commonNames.map((row) => (
								<TableRow key={row.nameId}>
									<TableCell>{row.cerName}</TableCell>
									<TableCell>{row.isActive === '1' || row.isActive === 'True' ? 'Active' : row.isActive || 'Active'}</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											onClick={() => handleDelete(row.nameId)}
											disabled={deleting}
											title="Remove common name"
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Paper>
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	)
}

export default withStyles(styles)(CommonNameManager)
